/**
 * THE CAPTURE SEAM, CLOSED — with the compatibility claim actually checked.
 * =======================================================================
 *
 * `src/forms/ui/capture-contract.tsx` states two prop interfaces and ships obvious placeholders,
 * saying: *"If `src/capture/` exports components with these props, the only change needed here is
 * swapping the two `use…` defaults for real imports."*
 *
 * **I CHECKED, AND THAT IS NOT THE CASE. The two components are not drop-in.** The *values* are
 * compatible exactly as documented; the *component props* are not. Precisely:
 *
 * ── PHOTO ────────────────────────────────────────────────────────────────────────────────────────
 *   contract: `{ fieldId, label, value: readonly PhotoRef[], max?, store, onCaptured(one), onRemove(id) }`
 *   real:     `{ label, value: readonly PhotoAttachment[], onChange(all), max?, compress?, heavyBytes?, deps? }`
 *
 *   `PhotoAttachment` IS assignable to `PhotoRef` (it has `blobId`, `at`, `label?`) — that half of the
 *   claim holds. But assignability runs the wrong way for `value`: the form stores `PhotoRef`, and
 *   `PhotoCapture` needs the richer `PhotoAttachment` so it can print the compressed size under each
 *   thumbnail. And the callbacks differ in shape: one-at-a-time versus whole-list. Hence the mirror
 *   below.
 *
 *   `store` is also NOT called. `PhotoCapture` already writes through its own `BlobSink`
 *   (`capture/blobStore` → `core/blobs.putBlob`) and hands back a stored `PhotoAttachment`. Calling
 *   `store` as well would write the same bytes to IndexedDB twice and leak the first copy — which is
 *   exactly the case the contract's own comment anticipated: *"A component that already writes through
 *   its own sink … can ignore this and call `onCaptured` directly."*
 *
 * ── SERIAL ───────────────────────────────────────────────────────────────────────────────────────
 *   contract: `{ fieldId, label, value?: SerialValue, reference?, onCaptured(one), minLength? }`
 *   real:     `{ title?, context?, expectedCount?, initialCaptures?, rules?, onChange?, onDone(many), … }`
 *
 *   Not close. `SerialScanner` is a **bulk** sheet — camera stays open, list grows, `onDone` returns
 *   every serial in capture order — because a supplier at a warehouse works down a pallet. A schema
 *   `serial` field is ONE serial (`module01_serial` … `module20_serial`, each gated on
 *   `ctx.moduleCount`). `SerialCapture` IS assignable to `SerialValue`, so the value half of the claim
 *   holds here too; the arity does not.
 *
 *   **A bulk scan into a single-serial field must not silently keep the first read and drop the rest.**
 *   So: exactly one capture is adopted; more than one puts the list on screen and requires a tap to say
 *   which one belongs to this module. That is a couple of extra lines and it is the difference between
 *   "the app recorded the wrong module's serial" being possible and being impossible.
 *
 * Nothing in `src/forms/` or `src/capture/` was changed to make this work. These two components
 * satisfy the contract's interfaces exactly, so `FormRenderer`'s `PhotoCapture` / `SerialCapture`
 * props take them and `FieldControl` is untouched.
 */

import { useState } from 'react';
import { CameraGlyph, DEFAULT_SERIAL_RULES, PhotoCapture, SerialScanner } from '@/capture';
import type { PhotoAttachment, SerialCapture, SerialRules } from '@/capture';
import type { PhotoCaptureProps, SerialCaptureProps } from '@/forms';

/* ------------------------------------------------------------------ photo ---- */

export function PhotoCaptureField(props: PhotoCaptureProps): JSX.Element {
  const { fieldId, label, value, onCaptured, onRemove } = props;

  /**
   * The rich attachments captured in this mounting, keyed by the same `blobId` the form holds.
   *
   * A mirror is needed because the two components hold different amounts of the same fact: the form
   * keeps `{ blobId, at, label }` (all it needs), and `PhotoCapture` needs the pixel dimensions and
   * the compressed byte count to render its thumbnail meta. The form state is the authority for WHICH
   * photos exist; this is only extra detail about them.
   */
  const [rich, setRich] = useState<readonly PhotoAttachment[]>([]);

  const byId = new Map(rich.map((attachment) => [attachment.blobId, attachment]));
  const shown = value.flatMap((ref) => {
    const found = byId.get(ref.blobId);
    return found === undefined ? [] : [found];
  });
  /**
   * Photos the form knows about but this mounting did not capture.
   *
   * Reachable only if form state is ever restored from storage (it is not today — a half-filled form
   * does not survive a reload; the outbox does). Listed plainly rather than rendered as a thumbnail
   * with `0 B` and `0×0`, which would be inventing measurements.
   */
  const detailUnknown = value.filter((ref) => !byId.has(ref.blobId));

  const handleChange = (next: readonly PhotoAttachment[]): void => {
    const before = new Set(shown.map((attachment) => attachment.blobId));
    const after = new Set(next.map((attachment) => attachment.blobId));
    setRich(next);
    // `PhotoAttachment` → `PhotoRef` with no conversion: the assignability the contract promised.
    for (const attachment of next) {
      if (!before.has(attachment.blobId)) onCaptured(attachment);
    }
    for (const attachment of shown) {
      if (!after.has(attachment.blobId)) onRemove(attachment.blobId);
    }
  };

  const body = (
    <>
      <PhotoCapture
        label={label}
        value={shown}
        onChange={handleChange}
        {...(props.max !== undefined ? { max: props.max } : {})}
      />
      {detailUnknown.length > 0 && (
        <ul className="fx-photo-list">
          {detailUnknown.map((ref) => (
            <li key={ref.blobId}>
              <span className="fx-photo-id">
                Attached earlier ({ref.blobId}) — size and dimensions not available in this session.
              </span>
              <button type="button" className="button" onClick={() => onRemove(ref.blobId)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );

  // `<fieldset disabled>` rather than threading a `disabled` prop the real component does not have:
  // it disables every descendant control natively, including the two hidden file inputs.
  return props.disabled === true ? (
    <fieldset className="fx-photo" data-field={fieldId} disabled>
      {body}
    </fieldset>
  ) : (
    <div className="fx-photo" data-field={fieldId}>
      {body}
    </div>
  );
}

/* ----------------------------------------------------------------- serial ---- */

function describeMethod(value: { method: 'scan' | 'manual'; format?: string | null }): string {
  return value.method === 'scan' ? `scanned${value.format ? ` (${value.format})` : ''}` : 'typed';
}

export function SerialCaptureField(props: SerialCaptureProps): JSX.Element {
  const { fieldId, label, value, reference, onCaptured } = props;
  const [scanning, setScanning] = useState(false);
  /** More than one serial came back from a bulk scan; the technician must say which one this is. */
  const [choices, setChoices] = useState<readonly SerialCapture[]>([]);

  const rules: SerialRules =
    props.minLength === undefined
      ? DEFAULT_SERIAL_RULES
      : { ...DEFAULT_SERIAL_RULES, minLength: props.minLength };

  const adopt = (capture: SerialCapture): void => {
    // `SerialCapture` → `SerialValue`: structurally assignable, so no field renaming and no risk of
    // `method` or `format` being reconstructed (and therefore possibly falsified) on the way across.
    onCaptured(capture);
    setChoices([]);
    setScanning(false);
  };

  const finish = (captures: readonly SerialCapture[]): void => {
    setScanning(false);
    if (captures.length === 0) return;
    const only = captures[0];
    if (captures.length === 1 && only !== undefined) {
      adopt(only);
      return;
    }
    setChoices(captures);
  };

  if (scanning) {
    return (
      <div className="fx-serial" data-field={fieldId}>
        <SerialScanner
          title={label}
          context="This question records one serial."
          expectedCount={1}
          rules={rules}
          onDone={finish}
          onCancel={() => setScanning(false)}
        />
      </div>
    );
  }

  return (
    <div className="fx-serial" data-field={fieldId}>
      {reference !== undefined && (
        <p className="fx-reference">
          {/*
            The closing clause is promoted to `.fx-prov-why` so this note reads the same as
            `ReferenceNote`'s — the one class in the provenance system whose whole job is to say, in the
            heaviest available weight, that the value on screen is NOT the answer. Without it the "treat
            this as something to check" clause was the tail of a sentence in the same weight as the
            serial it was warning about.
          */}
          On the asset record: <code>{reference}</code>. Roughly 60% of stored serials are migration
          placeholders.
          <span className="fx-prov-why">
            Check it against the plate rather than trusting it. This is not your answer.
          </span>
        </p>
      )}

      {value === undefined ? (
        <p className="fx-hint">Nothing recorded yet.</p>
      ) : (
        <p className="fx-serial-current">
          <strong>{value.serial}</strong> <span className="fx-hint">({describeMethod(value)})</span>
        </p>
      )}

      {choices.length > 0 && (
        <div className="fx-serial-choices" role="group" aria-label={`Choose the serial for ${label}`}>
          <p className="fx-hint">
            {choices.length} serials were scanned and this question records one. Tap the one for{' '}
            {label}. Nothing has been recorded yet.
          </p>
          <ul>
            {choices.map((capture) => (
              <li key={capture.id}>
                <button type="button" className="button" onClick={() => adopt(capture)}>
                  Use {capture.serial} ({describeMethod(capture)})
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="button" onClick={() => setChoices([])}>
            None of these
          </button>
        </div>
      )}

      {/*
        `fx-big`, not `button`. This is the primary — and only — way to answer a serial question, and it
        was rendered in the same low-emphasis treatment as "Cancel". The label already leads with both
        paths ("Scan OR TYPE serial") because the sheet it opens starts with the camera off and the text
        field on screen, so typing is never behind a scan that has to fail first; the note below says so
        explicitly, since a technician holding a scratched module plate should not have to discover that
        by tapping a button labelled "Scan".

        FIELD FEEDBACK 5: the words alone did not read as a scan action, so the target now carries a
        camera glyph and the note LEADS with scanning. The both-paths framing above is deliberate and
        survives — the accessible name still says "Scan or type", and typing is still described as a
        primary path, not a fallback. The sheet still opens with the camera OFF: that default is also
        deliberate (`SerialScanner.start()` asks for camera permission at the point of use because an
        iOS Safari denial is expensive to undo), so the affordance is fixed visually rather than by
        auto-starting the camera into a permission prompt the technician did not ask for.
      */}
      <button
        type="button"
        className="fx-big"
        disabled={props.disabled === true}
        onClick={() => setScanning(true)}
      >
        <span className="fx-big__title">
          <CameraGlyph />
          {value === undefined ? 'Scan or type serial' : 'Change serial'}
        </span>
        <span className="fx-big__note">
          Scan the plate with the camera — it starts with one tap inside. A text field is ready too:
          typing is not a fallback, use it whenever the label is damaged or the light is bad.
        </span>
      </button>
    </div>
  );
}
